import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Row,
  Col,
  Typography,
  Tag,
  Input,
  Select,
  Button,
  Spin,
  Empty,
  message,
  Space,
  Modal,
  Descriptions,
  Badge,
  Tooltip,
  Divider,
} from 'antd';
import {
  SearchOutlined,
  ImportOutlined,
  GithubOutlined,
  GlobalOutlined,
  CloudOutlined,
  ArrowLeftOutlined,
  StarOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import styled from 'styled-components';
import { scarApi } from '../services/api';

const { Title, Paragraph, Text } = Typography;
const { Search } = Input;
const { Option } = Select;

const PageHeader = styled.div`
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 16px;
`;

const SourceTag = styled(Tag)`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 12px;
  font-size: 14px;
`;

const SkillCard = styled(Card)`
  transition: all 0.3s;
  height: 100%;
  
  &:hover {
    box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  }
`;

const ImportButton = styled(Button)`
  margin-top: 12px;
  width: 100%;
`;

// 源类型图标映射
const SOURCE_ICONS = {
  github: <GithubOutlined />,
  gitlab: <CloudOutlined />,
  openclaw: <GlobalOutlined />,
  url: <GlobalOutlined />,
  api: <CloudOutlined />,
};

// 源类型颜色
const SOURCE_COLORS = {
  github: 'default',
  gitlab: 'orange',
  openclaw: 'purple',
  url: 'green',
  api: 'blue',
};

function SkillImport() {
  const navigate = useNavigate();
  const [sources, setSources] = useState([]);
  const [selectedSource, setSelectedSource] = useState(null);
  const [keyword, setKeyword] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState({}); // { [external_id]: boolean }
  const [imported, setImported] = useState({});   // { [external_id]: { success, skillId } }
  const [detailModal, setDetailModal] = useState({ visible: false, skill: null });
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    fetchSources();
  }, []);

  const fetchSources = async () => {
    try {
      const res = await scarApi.getSources();
      const list = res.data.data || [];
      setSources(list);
      if (list.length > 0 && !selectedSource) {
        setSelectedSource(list[0].code);
      }
    } catch (error) {
      console.error('获取外部源失败:', error);
      message.error('获取外部源列表失败');
    }
  };

  const handleSearch = async (value) => {
    if (!value.trim()) {
      message.warning('请输入搜索关键词');
      return;
    }
    if (!selectedSource) {
      message.warning('请选择外部源');
      return;
    }

    try {
      setSearching(true);
      setLoading(true);
      setKeyword(value);
      const res = await scarApi.searchSkills(selectedSource, value.trim());
      setResults(res.data.data || []);
      if ((res.data.data || []).length === 0) {
        message.info('未找到匹配的技能');
      }
    } catch (error) {
      console.error('搜索失败:', error);
      message.error(error.response?.data?.message || '搜索失败');
    } finally {
      setLoading(false);
      setSearching(false);
    }
  };

  const handleImport = async (skill) => {
    try {
      setImporting(prev => ({ ...prev, [skill.external_id]: true }));
      const res = await scarApi.importSkill({
        source: skill.source_type,
        external_id: skill.external_id,
        external_url: skill.external_url,
        name: skill.name,
        display_name: skill.display_name || skill.name,
        description: skill.description,
        tags: skill.tags || [],
        version: skill.version || '1.0.0',
      });

      setImported(prev => ({
        ...prev,
        [skill.external_id]: { success: true, skillId: res.data.data.id },
      }));
      message.success(res.data.message || '导入成功');
    } catch (error) {
      const errMsg = error.response?.data?.message || '导入失败';
      if (error.response?.status === 409) {
        // 已导入
        setImported(prev => ({
          ...prev,
          [skill.external_id]: { success: true, skillId: error.response.data.data?.skillId },
        }));
        message.warning(errMsg);
      } else {
        message.error(errMsg);
      }
    } finally {
      setImporting(prev => ({ ...prev, [skill.external_id]: false }));
    }
  };

  const showDetail = (skill) => {
    setDetailModal({ visible: true, skill });
  };

  const getSourceInfo = (code) => sources.find(s => s.code === code);

  return (
    <div>
      <PageHeader>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/skills')}>
          返回商店
        </Button>
        <Title level={3} style={{ margin: 0 }}>
          <ImportOutlined /> SCAR 技能导入
        </Title>
      </PageHeader>

      <Paragraph type="secondary" style={{ marginBottom: 24 }}>
        从外部技能源（GitHub / GitLab / OpenClaw）搜索并导入技能到百音技能商店。导入后需等待管理员审核。
      </Paragraph>

      {/* 搜索栏 */}
      <Card style={{ marginBottom: 24 }}>
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Space wrap>
            <Text strong>选择来源：</Text>
            <Select
              value={selectedSource}
              onChange={setSelectedSource}
              style={{ width: 200 }}
              placeholder="选择外部源"
            >
              {sources.map(source => (
                <Option key={source.code} value={source.code}>
                  <Space>
                    {SOURCE_ICONS[source.type] || <GlobalOutlined />}
                    {source.name}
                  </Space>
                </Option>
              ))}
            </Select>
          </Space>

          <Search
            placeholder="搜索技能名称、标签、描述..."
            enterButton={<><SearchOutlined /> 搜索</>}
            size="large"
            onSearch={handleSearch}
            loading={searching}
            allowClear
          />

          <Space wrap>
            <Text type="secondary">快捷搜索：</Text>
            {['music', 'audio', 'marketing', 'data', 'lyric', 'cover'].map(tag => (
              <Tag
                key={tag}
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  setKeyword(tag);
                  handleSearch(tag);
                }}
              >
                {tag}
              </Tag>
            ))}
          </Space>
        </Space>
      </Card>

      {/* 搜索结果 */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '80px' }}>
          <Spin size="large" tip="正在搜索外部技能源..." />
        </div>
      ) : results.length === 0 ? (
        keyword ? (
          <Empty description={`未找到与 "${keyword}" 匹配的外部技能`} style={{ padding: '80px' }} />
        ) : (
          <Empty description="请输入关键词搜索外部技能" style={{ padding: '80px' }} />
        )
      ) : (
        <>
          <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text>
              找到 <Text strong>{results.length}</Text> 个结果
              {selectedSource && (
                <SourceTag color={SOURCE_COLORS[getSourceInfo(selectedSource)?.type] || 'default'}>
                  {SOURCE_ICONS[getSourceInfo(selectedSource)?.type]}
                  {getSourceInfo(selectedSource)?.name}
                </SourceTag>
              )}
            </Text>
          </div>

          <Row gutter={[16, 16]}>
            {results.map(skill => (
              <Col xs={24} sm={12} lg={8} key={`${skill.source_type}-${skill.external_id}`}>
                <SkillCard
                  title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {SOURCE_ICONS[skill.source_type] || <GlobalOutlined />}
                      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {skill.display_name || skill.name}
                      </span>
                      {skill.stars > 0 && (
                        <Tooltip title="Stars">
                          <Tag icon={<StarOutlined />} color="warning">
                            {skill.stars}
                          </Tag>
                        </Tooltip>
                      )}
                    </div>
                  }
                  extra={
                    <Tag color={SOURCE_COLORS[skill.source_type] || 'default'}>
                      {skill.source_type}
                    </Tag>
                  }
                >
                  <Paragraph ellipsis={{ rows: 2 }} type="secondary" style={{ minHeight: 44 }}>
                    {skill.description || '暂无描述'}
                  </Paragraph>

                  <Space wrap size="small" style={{ marginBottom: 12, minHeight: 24 }}>
                    {(skill.tags || []).slice(0, 4).map(tag => (
                      <Tag key={tag} size="small">{tag}</Tag>
                    ))}
                  </Space>

                  <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      v{skill.version || '1.0.0'}
                    </Text>
                    <Button type="link" size="small" onClick={() => showDetail(skill)}>
                      详情
                    </Button>
                  </Space>

                  {imported[skill.external_id]?.success ? (
                    <ImportButton type="default" disabled icon={<CheckCircleOutlined />}>
                      已导入
                    </ImportButton>
                  ) : (
                    <ImportButton
                      type="primary"
                      icon={<ImportOutlined />}
                      loading={importing[skill.external_id]}
                      onClick={() => handleImport(skill)}
                    >
                      导入本地
                    </ImportButton>
                  )}
                </SkillCard>
              </Col>
            ))}
          </Row>
        </>
      )}

      {/* 详情弹窗 */}
      <Modal
        title="技能详情"
        open={detailModal.visible}
        onCancel={() => setDetailModal({ visible: false, skill: null })}
        footer={[
          <Button key="close" onClick={() => setDetailModal({ visible: false, skill: null })}>
            关闭
          </Button>,
          detailModal.skill && !imported[detailModal.skill.external_id]?.success && (
            <Button
              key="import"
              type="primary"
              icon={<ImportOutlined />}
              loading={importing[detailModal.skill?.external_id]}
              onClick={() => {
                handleImport(detailModal.skill);
                setDetailModal({ visible: false, skill: null });
              }}
            >
              导入本地
            </Button>
          ),
        ].filter(Boolean)}
        width={600}
      >
        {detailModal.skill && (
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="名称">
              {detailModal.skill.display_name || detailModal.skill.name}
            </Descriptions.Item>
            <Descriptions.Item label="标识">
              {detailModal.skill.name}
            </Descriptions.Item>
            <Descriptions.Item label="来源">
              <Tag color={SOURCE_COLORS[detailModal.skill.source_type]}>
                {detailModal.skill.source_type}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="版本">
              v{detailModal.skill.version || '1.0.0'}
            </Descriptions.Item>
            <Descriptions.Item label="星标">
              {detailModal.skill.stars || 0} ⭐
            </Descriptions.Item>
            <Descriptions.Item label="描述">
              {detailModal.skill.description || '暂无描述'}
            </Descriptions.Item>
            <Descriptions.Item label="标签">
              <Space wrap>
                {(detailModal.skill.tags || []).map(tag => (
                  <Tag key={tag}>{tag}</Tag>
                ))}
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="外部链接">
              <a href={detailModal.skill.external_url} target="_blank" rel="noopener noreferrer">
                {detailModal.skill.external_url}
              </a>
            </Descriptions.Item>
            {detailModal.skill.matched_file && (
              <Descriptions.Item label="匹配文件">
                {detailModal.skill.matched_file}
              </Descriptions.Item>
            )}
          </Descriptions>
        )}
      </Modal>
    </div>
  );
}

export default SkillImport;
